import React, { useState, useEffect, useRef } from "react";
import {
  FormControl,
  VStack,
  useColorModeValue,
  Flex,
  Box,
  useToast,
} from "@chakra-ui/react";
import InputLabel from "../inputs/InputLabel";
import SmallInputText from "../inputs/SmallInputText";
import SmallButton from "../buttons/SmallButton";
import SmallSelectInput from "../inputs/SmallSelectInput";
import { checkValidEmail } from "../../utils/formatting";
import { User } from "../../utils/api-data-types";
import useApi from "../../hooks/useApi";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  refreshUsers: () => Promise<void>;
};

const CreateUserModal: React.FC<Props> = ({
  isOpen,
  onClose,
  refreshUsers,
}) => {
  const { addUser } = useApi();
  const toast = useToast();
  const [canSubmit, setCanSubmit] = useState<boolean>(false);
  const [email, setEmail] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [role, setRole] = useState<"VIEW_ONLY" | "ADMIN">("VIEW_ONLY");

  useEffect(() => {
    const isValidEmail = email && checkValidEmail(email);
    if (isValidEmail && name && role) {
      setCanSubmit(true);
    } else {
      setCanSubmit(false);
    }
  }, [email, name, role]);

  const modalRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  const modalBackgroundColor = useColorModeValue(
    "white",
    "dark.modalBackgroundColor"
  );

  const noBackgroundButtonTextColor = useColorModeValue(
    "light.noBackgroundButtonTextColor",
    "dark.noBackgroundButtonTextColor"
  );

  const modalHeaderBorderColor = useColorModeValue(
    "light.modalHeaderBorderColor",
    "dark.modalHeaderBorderColor"
  );

  const handleAddUser = async () => {
    const user = { email, name, role };
    const { error } = await addUser(user as User);
    if (error) {
      toast({
        position: "top",
        title: "Error",
        description: error,
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    }

    toast({
      position: "top",
      title: "Success",
      description: "User created.",
      status: "success",
      duration: 9000,
      isClosable: true,
    });

    await refreshUsers();

    onClose();
  };

  return (
    <VStack
      display={isOpen ? "flex" : "none"}
      position="fixed"
      top={0}
      left={0}
      right={0}
      bottom={0}
      justifyContent="center"
      alignItems="center"
      bgColor="rgba(0, 0, 0, 0.5)"
      zIndex="9999"
    >
      <FormControl
        ref={modalRef}
        bgColor={modalBackgroundColor}
        w="600px"
        borderRadius="md"
        boxShadow="md"
        p={0}
        minWidth="400px"
        onSubmit={handleAddUser}
      >
        <Flex
          bgColor={modalHeaderBorderColor}
          justify="center"
          fontSize="xl"
          borderTopRadius="md"
          py={4}
          mb={4}
          fontWeight="medium"
        >
          Create User
        </Flex>
        <Box pb={12} px={12}>
          <VStack w="full" spacing="16px">
            <InputLabel w="full">
              Name
              <SmallInputText
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
                mt="8px"
              />
            </InputLabel>
            <InputLabel w="full">
              Email
              <SmallInputText
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                mt="8px"
              />
            </InputLabel>
            <InputLabel w="full">
              User Role
              <SmallSelectInput
                value={role}
                onChange={(e: any) => setRole(e.target.value)}
                mt="8px"
              >
                <option value="VIEW_ONLY">View Only</option>
                <option value="ADMIN">Admin</option>
              </SmallSelectInput>
            </InputLabel>
          </VStack>
          <Box mt={4}>
            <SmallButton disabled={!canSubmit} mt={4} onClick={handleAddUser}>
              Add User
            </SmallButton>
            <SmallButton
              color={noBackgroundButtonTextColor}
              bgColor="none"
              mt={4}
              ml={4}
              onClick={onClose}
            >
              Cancel
            </SmallButton>
          </Box>
        </Box>
      </FormControl>
    </VStack>
  );
};

export default CreateUserModal;
